// @spec POL-STR-014
'use client';

import { createFileRoute, Link, useNavigate, useSearch } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTemplates } from '@/features/policies/api/document-templates';
import { useCreatePolicy } from '@/features/policies/api/mutations';
import { usePolicyCategories } from '@/features/policies/api/policy-categories';
import { useFrameworks } from '@/features/policies/api/use-policies';
import { PolicyForm, type PolicyFormValues } from '@/features/policies/components/PolicyForm';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

// URL search params schema
const createPolicySearchSchema = z.object({
  templateId: z.string().optional(),
});

export const Route = createFileRoute('/_authenticated/policies/create')({
  validateSearch: createPolicySearchSchema,
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.createPolicy'),
  }),
  component: CreatePolicyPage,
});

/**
 * Create Policy page - Form for creating new policies.
 *
 * Features:
 * - Full-featured policy creation form
 * - Rich text editor for content
 * - Category and framework selection
 * - Template pre-fill via URL params
 */
function CreatePolicyPage() {
  const { t } = useTranslation('policies');
  const navigate = useNavigate();
  const searchParams = useSearch({ from: '/_authenticated/policies/create' });
  const templateId = searchParams.templateId;

  // Data queries
  const { data: categoriesData, isLoading: isCategoriesLoading } = usePolicyCategories();
  const { data: frameworksData, isLoading: isFrameworksLoading } = useFrameworks();
  const { data: templatesData, isLoading: isTemplatesLoading } = useDocumentTemplates();

  // Create mutation
  const createPolicy = useCreatePolicy();

  // Find template if provided in URL
  const template = React.useMemo(() => {
    if (!templateId || !templatesData?.items) return null;
    return templatesData.items.find((t) => t.id === templateId) ?? null;
  }, [templateId, templatesData?.items]);

  // Build default values from template
  const defaultValues: PolicyFormValues | undefined = React.useMemo(() => {
    if (!template) return undefined;

    // Map DocumentTemplateType to PolicyType (both are compatible)
    const policyType = template.documentType as PolicyFormValues['type'];

    return {
      title: '',
      description: '',
      type: policyType,
      categoryId: template.defaultCategoryId ?? '',
      content: template.content ?? '',
      effectiveDate: '',
      reviewFrequency: 'annual',
      frameworkIds: template.defaultFrameworkIds,
      tags: [],
    };
  }, [template]);

  const isLoading = isCategoriesLoading || isFrameworksLoading || isTemplatesLoading;

  const handleSubmit = async (data: PolicyFormValues) => {
    try {
      // @fixed type-safety-templateId - Convert undefined to null for API
      const result = await createPolicy.mutateAsync({
        title: data.title,
        description: data.description || null,
        type: data.type,
        categoryId: data.categoryId || null,
        content: data.content || null,
        effectiveDate: data.effectiveDate || null,
        reviewFrequency: data.reviewFrequency,
        frameworkIds: data.frameworkIds,
        tags: data.tags,
        templateId: templateId ?? null,
        ...(data.complianceMappings && data.complianceMappings.length > 0
          ? { complianceMappings: data.complianceMappings }
          : {}),
      });

      toast.success(t('policies:toast.policyCreatedSuccessfully'), {
        description: t('policies:wizard.review.createdDraftToast', { title: result.title }),
      });

      void navigate({
        to: '/policies/$policyId',
        params: { policyId: result.id },
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : t('policies:wizard.review.unexpectedError');
      toast.error(t('policies:toast.failedToCreatePolicy'), {
        description: message,
      });
    }
  };

  const handleCancel = () => {
    void navigate({ to: '/policies' });
  };

  if (isLoading) {
    return (
      <div className="container max-w-4xl py-6">
        <div className="mb-6 space-y-2">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-6 w-64" />
        </div>
        <div className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  const categories = categoriesData?.items ?? [];
  const frameworks = frameworksData ?? [];

  return (
    <div className="container max-w-4xl py-6">
      {/* Header */}
      <div className="mb-6">
        <div className="mb-4">
          <Button variant="ghost" size="sm" asChild className="-ml-2">
            <Link to="/policies">
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('policies:wizard.backToPolicies')}
            </Link>
          </Button>
        </div>
        <h1 className="text-2xl font-semibold tracking-tight">
          {t('policies:routeTitles.createPolicy')}
        </h1>
        <p className="text-muted-foreground">{t('policies:createPage.description')}</p>
        {template && (
          <p className="mt-2 text-sm text-muted-foreground">
            <span className="font-medium">
              {t('policies:createPage.usingTemplate', { name: template.name })}
            </span>
          </p>
        )}
      </div>

      {/* Form */}
      {/* @fixed type-safety-defaultValues - Use empty object when no template */}
      <div className="rounded-lg border bg-card p-6">
        <PolicyForm
          defaultValues={defaultValues ?? {}}
          categories={categories}
          frameworks={frameworks}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isSubmitting={createPolicy.isPending}
          mode="create"
        />
      </div>
    </div>
  );
}
